import { useState } from "react";
import { Link } from "wouter";
import { Terminal, ArrowRight, Loader2, Check } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Something went wrong");
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex flex-col">
      <style>{`
        .neon-glow-cyan { text-shadow: 0 0 20px rgba(0, 255, 255, 0.8); }
        .cursor-blink { animation: blink 1s step-end infinite; }
        @keyframes blink { from, to { opacity: 1; } 50% { opacity: 0; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
      `}</style>

      <header className="border-b border-white/5 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link href="/">
            <span className="text-xl font-black tracking-tighter text-cyan-400 neon-glow-cyan cursor-pointer">DRPR</span>
          </Link>
          <Link href="/join">
            <span className="text-[11px] uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors cursor-pointer">Back to Join</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-white/25 mb-8">
            <Terminal size={12} className="text-cyan-400" />
            <span>Password Reset</span>
            <span className="w-2 h-3 bg-cyan-400/50 cursor-blink ml-1"></span>
          </div>

          <h1 className="text-3xl font-black uppercase tracking-tighter mb-3">
            FORGOT <span className="text-cyan-400 neon-glow-cyan">PASSWORD?</span>
          </h1>
          <p className="text-sm text-white/40 font-sans mb-10">
            Enter your email and we'll send you a reset link.
          </p>

          {submitted ? (
            <div className="fade-up flex items-start gap-3 bg-cyan-400/5 border border-cyan-400/20 p-5">
              <Check size={14} className="text-cyan-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-1">Check your email</p>
                <p className="text-[11px] text-white/40 font-sans">If an account exists for <strong className="text-white/60">{email}</strong>, we've sent a reset link. Check your inbox (and spam folder).</p>
                <Link href="/join">
                  <span className="mt-4 inline-block text-[10px] text-white/30 hover:text-cyan-400 transition-colors uppercase tracking-widest cursor-pointer">Back to sign in</span>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-cyan-400/40 transition-colors"
                data-testid="input-forgot-email"
              />
              {error && (
                <p className="text-[11px] text-red-400 uppercase tracking-widest" data-testid="text-forgot-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-cyan-400/10 border border-cyan-400/40 text-cyan-400 text-xs font-bold uppercase tracking-widest hover:bg-cyan-400 hover:text-black transition-all disabled:opacity-50"
                data-testid="button-forgot-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : (
                  <>Send Reset Link <ArrowRight size={12} /></>
                )}
              </button>
              <Link href="/join">
                <span className="text-[10px] text-white/25 hover:text-white/50 transition-colors uppercase tracking-widest cursor-pointer text-center block mt-1">
                  Back to sign in
                </span>
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
